import {RefCountableBase} from "@cm/utils"
import {Three as THREE, ThreeTsl as TSL} from "#three-texture-streaming/three-exports"
import {TEXTURE_TILE_SIZE, TRACE_TILED_TEXTURES} from "#three-texture-streaming/texture-tile-constants"
import {MAX_TILED_TEXTURE_DESC_IN_SHADER, TILE_REQUEST_BUFFER_SIZE_SCALE} from "#three-texture-streaming/three-viewport-texture-tile-updater-constants"
import type {ExplicitTextureStreamingMode} from "#three-texture-streaming/types"
import {getComputeMipLevelTsl, splitMipLevelTsl} from "#three-texture-streaming/shader-helpers"
import type {ThreeTiledTextureInstance} from "#three-texture-streaming/three-tiled-texture-instance"
import {sceneLogger as logger} from "#three-texture-streaming/scene-logger"

const TRACE = TRACE_TILED_TEXTURES

type Vec2Node = THREE.Node<"vec2">

export class ThreeViewportTextureTileUpdaterMaterial extends RefCountableBase {
    constructor(
        _renderer: THREE.WebGPURenderer,
        uvNode: THREE.Node,
        readonly referencedTiledTextures: ThreeTiledTextureInstance[],
        opacity: number,
        streamingMode: ExplicitTextureStreamingMode,
        maxPixelsPerTexel: number,
    ) {
        super({
            disposeFn: () => this.dispose(),
        })
        const tiledTextureIds: number[] = []
        const tileNumMipLevels: number[] = []
        const minMipLevels: number[] = []
        const textureSizes: THREE.Vector2[] = []
        for (let i = 0; i < MAX_TILED_TEXTURE_DESC_IN_SHADER; i++) {
            if (i < referencedTiledTextures.length) {
                const tiledTextureInstance = referencedTiledTextures[i]
                const tiledTexture = tiledTextureInstance.tiledTexture
                if (tiledTexture.id <= 0) {
                    throw new Error("Unexpected tiledTexture with invalid id")
                }
                const imageTilesDescriptor = tiledTexture.imageTilesDescriptor
                tiledTextureIds.push(tiledTexture.id)
                tileNumMipLevels.push(tiledTexture.numMipLevels)
                minMipLevels.push(tiledTextureInstance.minMipLevel)
                textureSizes.push(new THREE.Vector2(imageTilesDescriptor.imageWidth, imageTilesDescriptor.imageHeight))
            } else {
                tiledTextureIds.push(0)
                tileNumMipLevels.push(0)
                minMipLevels.push(0)
                textureSizes.push(new THREE.Vector2(0, 0))
            }
        }

        // TSL uniforms
        this._frameIndexNode = TSL.uniform(0)
        const requestBufferScaleNode = TSL.uniform(TILE_REQUEST_BUFFER_SIZE_SCALE)
        const numTiledTexturesNode = TSL.uniform(referencedTiledTextures.length)
        const tiledTextureIdsNode = TSL.uniformArray(tiledTextureIds, "uint" as const)
        const tileNumMipLevelsNode = TSL.uniformArray(tileNumMipLevels, "uint" as const)
        const minMipLevelsNode = TSL.uniformArray(minMipLevels, "uint" as const)
        const textureSizesNode = TSL.uniformArray(textureSizes, "vec2" as const)
        const opacityNode = TSL.uniform(opacity)

        const tileSize = TSL.vec2(TEXTURE_TILE_SIZE)
        const uvIn = uvNode as Vec2Node
        const maxPixelsPerTexelNode = TSL.float(maxPixelsPerTexel)

        const computeMipLevelTsl = getComputeMipLevelTsl(streamingMode)
        const fragmentFn = TSL.Fn(() => {
            // Flip Y: tile data has origin at top-left, UV origin is bottom-left
            const uv = TSL.vec2(uvIn.x, TSL.float(1).sub(uvIn.y))

            // Compute derivatives BEFORE any branching
            const ddx = TSL.dFdx(uv).toVar()
            const ddy = TSL.dFdy(uv).toVar()

            // Seeded noise from screen coordinate and frame index
            const seed = TSL.fract(TSL.sin(TSL.dot(TSL.screenCoordinate.xy.add(TSL.float(this._frameIndexNode)), TSL.vec2(12.9898, 78.233))).mul(43758.5453))
            const rand1 = TSL.fract(TSL.sin(seed.mul(123.456)).mul(789.012))
            const rand2 = TSL.fract(TSL.sin(rand1.mul(123.456)).mul(789.012))

            // Stochastic opacity: discard fragment with probability (1 - opacity)
            TSL.If(opacityNode.lessThan(TSL.float(1.0)).and(opacityNode.lessThan(rand1)), () => {
                TSL.Discard()
            })

            const encodedTile = TSL.uint(0).toVar()

            TSL.If(numTiledTexturesNode.greaterThan(TSL.float(0)), () => {
                const randomIndex = TSL.int(TSL.floor(rand2.mul(numTiledTexturesNode)))

                const tiledTextureId = tiledTextureIdsNode.element(randomIndex).toVar()
                const numMipLevels = tileNumMipLevelsNode.element(randomIndex).toVar()
                const minMipLevelU = minMipLevelsNode.element(randomIndex).toVar()
                const textureSize = textureSizesNode.element(randomIndex).toVar()

                const minMipLevelF = TSL.float(minMipLevelU)
                const maxMipLevelF = TSL.float(numMipLevels.sub(TSL.uint(1)))

                // Scale derivatives by texture size and request buffer scale (corrects for downsampled buffer resolution)
                const ddxScaled = ddx.mul(textureSize).mul(requestBufferScaleNode)
                const ddyScaled = ddy.mul(textureSize).mul(requestBufferScaleNode)
                const mipLevelF = computeMipLevelTsl(ddxScaled, ddyScaled, minMipLevelF, maxMipLevelF, maxPixelsPerTexelNode)
                const {mipLevelInt} = splitMipLevelTsl(mipLevelF, maxMipLevelF)
                const mipLevelU = mipLevelInt.toUint()

                const numTiles = textureSize.div(tileSize)
                const mipLevelScale = TSL.float(1).div(TSL.float(TSL.uint(1).shiftLeft(mipLevelU))) // 1 / 2^mip
                const tileCoords = TSL.floor(TSL.fract(uv).mul(numTiles).mul(mipLevelScale))
                const tileIndexX = TSL.uint(tileCoords.x)
                const tileIndexY = TSL.uint(tileCoords.y)

                // Pack: tiledTextureId[11:0] << 20 | mipLevel[3:0] << 16 | tileY[7:0] << 8 | tileX[7:0]
                const encoded = tiledTextureId
                    .bitAnd(TSL.uint(0xfff))
                    .shiftLeft(TSL.uint(20))
                    .bitOr(mipLevelU.bitAnd(TSL.uint(0xf)).shiftLeft(TSL.uint(16)))
                    .bitOr(tileIndexY.bitAnd(TSL.uint(0xff)).shiftLeft(TSL.uint(8)))
                    .bitOr(tileIndexX.bitAnd(TSL.uint(0xff)))

                encodedTile.assign(encoded)
            })

            // Pack uint32 tile ID into RGBA bytes (normalized to [0,1]) to work around Three.js
            // not supporting readback of RED_INTEGER format (missing in _getBytesPerTexel).
            return TSL.vec4(
                TSL.float(encodedTile.bitAnd(TSL.uint(0xff))).div(255.0),
                TSL.float(encodedTile.shiftRight(TSL.uint(8)).bitAnd(TSL.uint(0xff))).div(255.0),
                TSL.float(encodedTile.shiftRight(TSL.uint(16)).bitAnd(TSL.uint(0xff))).div(255.0),
                TSL.float(encodedTile.shiftRight(TSL.uint(24)).bitAnd(TSL.uint(0xff))).div(255.0),
            )
        })

        const material = new THREE.NodeMaterial()
        material.name = "ThreeViewportTextureTileUpdaterMaterial"
        material.lights = false
        material.fog = false
        material.transparent = false
        material.blending = THREE.NoBlending
        material.toneMapped = false
        material.fragmentNode = fragmentFn()
        material.needsUpdate = true

        this._material = material
    }

    dispose(): void {
        if (TRACE) {
            logger.log("ThreeViewportTextureTileUpdaterMaterial: Disposing", this)
        }
        this._material.dispose()
    }

    updateFrameIndex(frameIndex: number): void {
        this._frameIndexNode.value = frameIndex
        this._frameIndexNode.needsUpdate = true
    }

    get material(): THREE.NodeMaterial {
        return this._material
    }

    private readonly _material: THREE.NodeMaterial
    private readonly _frameIndexNode: THREE.UniformNode<"float", number>
}
